import React, { useMemo } from 'react'
import styled from 'styled-components'
import { Panel } from 'primereact/panel'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'

import {
    ChargePoint,
    useChargePointRebootMutation,
    useChargePointUpdateMutation,
    GreenChargingChargePointStatus,
} from '../../common/redux/api/web-api'
import Text from '../../ui/Text'
import { isSupportDomain, isUserappDomain } from '../currentDomain'
import { GreenChargingIcon } from '../icons/green-charging-icon'
import isOnline from '../isOnline'
import useText from '../../common/hooks/useText'
import { useToast } from '../../common/uikit/ToastProvider'

const SGreenChargingIconWrapper = styled.span`
    padding-left: 8px;
    align-self: center;
`

export type RemoteControlWidgetProps = {
    children: React.ReactNode
    greenChargingStatus?: GreenChargingChargePointStatus['greenChargingStatus']
    isReadOnly: boolean
    chargePoint: ChargePoint
    title: string
    showNameInTitle?: boolean
}

export function RemoteControlWidget({
    children,
    greenChargingStatus,
    isReadOnly,
    chargePoint,
    showNameInTitle = isUserappDomain,
}: RemoteControlWidgetProps): React.ReactElement {
    const text = useText()
    const [updateChargePoint, { isLoading: chargePointUpdating }] = useChargePointUpdateMutation()
    const toast = useToast()

    const isChargerOnline = useMemo(() => {
        return isOnline(chargePoint)
    }, [chargePoint])

    const [startChargePointReboot] = useChargePointRebootMutation()
    const startReboot = async () => {
        try {
            await startChargePointReboot({
                chargePointId: chargePoint.id,
            })
                .unwrap()
                .then(() => {
                    toast.notifySuccess(text('msgRebootRequestAlert'))
                })
        } catch (error) {
            toast.notifyError(text('msgRebootRequestFail'))
        }
    }

    const startTest = async () => {
        try {
            await updateChargePoint({ chargePoint: { ...chargePoint, startTest: true } })
                .unwrap()
                .then(() => {
                    toast.notifySuccess(text('msgStartTestSuccess'))
                })
        } catch (error) {
            toast.notifyError(text('msgStartTestFail'))
        }
    }

    const rebootChargerConfirmation = () => {
        confirmDialog({
            message: text('msgRebootChargerConfirm'),
            header: text('msgRebootingCharger'),
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => startReboot(),
        })
    }

    const testChargerConfirmation = () => {
        confirmDialog({
            message: text('msgStartTestConfirm'),
            header: text('lblStartTestTitle'),
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => startTest(),
        })
    }
    return (
        <Panel
            data-testid="remote-control"
            header={
                <span className="mt-2 flex">
                    <span className="mr-2">
                        {showNameInTitle ? chargePoint.name : <Text code="lblRemoteControlPage" />}
                    </span>

                    {greenChargingStatus?.greenChargingOn && (
                        <SGreenChargingIconWrapper data-testid="greenCharging">
                            <GreenChargingIcon size={21} color={isChargerOnline ? '#006C5C' : '#808080'} />
                        </SGreenChargingIconWrapper>
                    )}
                </span>
            }
            toggleable
            style={chargePoint?.isOcpp && (!isChargerOnline || chargePoint?.ocppEnabled) ? { opacity: '50%' } : {}}
        >
            {chargePoint ? children : null}

            <div className="flex justify-content-end mr-2 gap-2 mt-3">
                {isSupportDomain &&
                    isChargerOnline &&
                    !chargePoint.isOcpp &&
                    chargePoint.productId &&
                    chargePoint.productId > 2 && (
                        <Button
                            label={text('btnTest')}
                            disabled={chargePoint.startTest}
                            onClick={testChargerConfirmation}
                            loading={chargePoint.startTest}
                            data-testid="test-btn"
                        />
                    )}

                {!isReadOnly && (
                    <Button
                        disabled={
                            !isChargerOnline ||
                            chargePoint.reboot ||
                            chargePointUpdating ||
                            (chargePoint?.isOcpp && chargePoint?.ocppEnabled)
                        }
                        onClick={rebootChargerConfirmation}
                        label={text('btnReboot')}
                        loading={!chargePoint.isOcpp && chargePoint.reboot}
                        data-testid="reboot-btn"
                    />
                )}
            </div>
        </Panel>
    )
}
